import { useEffect, useRef, useState } from 'react'
import { FlightMap } from './FlightMap'
import { FlightCard } from './FlightCard'
import { HomeContentCards } from './HomeContentCards'
import { useTripGetMyNextFlight } from '../lib/hooks'
import { useAirportByIcao } from '../lib/hooks/airport'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import { useCurrentDeviceLocation } from '@/hooks/useCurrentDeviceLocation'

/**
 * Desktop web home: the interactive Apple MapKit JS map as a full-bleed
 * background with a left-aligned content column floating over it — a transparent
 * flight header (white text on the map) above a cream card panel. Mirrors the
 * Figma desktop home. Rendered only at desktop widths from `Home`.
 */
export function DesktopHome() {
  const { data: currentUser } = useCurrentCrewMember()
  // Query regardless of includeInSchedule — excluded crew can still hold flight
  // assignments, and the flight card shows them; the map must match.
  const { data: nextFlight, isLoading: isNextFlightLoading } =
    useTripGetMyNextFlight(currentUser?.id)
  const departureAirport = useAirportByIcao(nextFlight?.departureAirport)
  const arrivalAirport = useAirportByIcao(nextFlight?.arrivalAirport)

  const noFlight = !isNextFlightLoading && !nextFlight && !!currentUser
  const { data: deviceLocation } = useCurrentDeviceLocation(noFlight)

  const mapLocation =
    noFlight && deviceLocation
      ? {
          latitude: deviceLocation.latitude,
          longitude: deviceLocation.longitude,
          label: deviceLocation.cityName,
        }
      : undefined

  // Measure the content column's right edge (viewport coords — the map is
  // `fixed inset-0`, so they map 1:1 to padding) so the route/location is
  // framed in the unobstructed area right of the sidebar + cards.
  const contentColumnRef = useRef<HTMLDivElement>(null)
  const [contentRight, setContentRight] = useState(0)
  useEffect(() => {
    const el = contentColumnRef.current
    if (!el) return
    const measure = () =>
      setContentRight(Math.round(el.getBoundingClientRect().right))
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return (
    <div className="absolute inset-0">
      {/* Map is a full-viewport background — `fixed` so it spans the whole
          window (behind the inset sidebar), escaping the `<main>` frame. */}
      <div className="fixed inset-0 z-0">
        <FlightMap
          departure={departureAirport ?? undefined}
          arrival={arrivalAirport ?? undefined}
          departureDateTime={nextFlight?.departureDateTime}
          arrivalDateTime={nextFlight?.arrivalDateTime}
          location={mapLocation}
          interactive
          edgePadding={{
            // Keep the route/location clear of the content column floating
            // over the left of the map (falls back to a half-window guess
            // until the column has been measured).
            top: 96,
            left:
              contentRight > 0
                ? contentRight + 24
                : Math.min(768, Math.round(window.innerWidth * 0.5)),
            bottom: 96,
            right: 80,
          }}
        />
      </div>

      {/* Content overlay. `pointer-events-none` so the uncovered map stays
          pannable/zoomable; the column re-enables pointer events for itself. */}
      <div className="absolute inset-0 z-10 overflow-y-auto pointer-events-none">
        <div className="flex min-h-full items-end justify-start p-6">
          <div
            ref={contentColumnRef}
            className="pointer-events-auto w-full max-w-[720px] flex flex-col gap-4"
          >
            <FlightCard
              nextFlight={nextFlight}
              isLoading={isNextFlightLoading || !currentUser}
              temperatureUnit={currentUser?.temperatureUnit}
              variant="plain"
              fullWidth
            />

            <div className="bg-surface-brand-L1 rounded-3xl p-4 pb-8 flex flex-col gap-6 shadow-[0px_5px_30px_rgba(0,0,0,0.12)]">
              <HomeContentCards secondaryTwoColumn />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
